import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { OktaAuthStateService, OKTA_AUTH } from '@okta/okta-angular';
import { BehaviorSubject, Observable, combineLatest, of, throwError } from 'rxjs';
import { LoginService } from '../login.service';
import { ContractStatusType, IContract, IFamilyMember, IUserContract } from 'src/app/model/contract/IContract';
import { PreInitSettingsService } from './pre-init-setting.service';
import { catchError, filter, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { OktaGroup, OktaUserProfile, UserClaims } from 'src/app/model/okta/userClaims';
import { PGHOktaService } from '../pghokta.service';
import { StorageService } from '../storage.service';
import { isAfter, add } from 'date-fns';
import { ADPResourceService } from 'digital-core';
import { PGHAppLoadService } from '../pgh-app-load.service';
import OktaAuth from '@okta/okta-auth-js';

const OKTA_TOKEN_STORAGE = 'okta-token-storage';

export interface ISelectedContract extends IContract {
  isPrimary: boolean;
  isExpired: boolean;
  isCancelled: boolean;
  isSuspended: boolean;
  isPTPO: boolean;
  inactiveMembers?: IFamilyMember[];
  title?: string;
  startDate?: Date;
  isEprismContract: boolean;
  oktaPrimaryPhone: string;
}

@Injectable({
  providedIn: 'root'
})
export class LoggedInUserSettings {
  private middleWareUrl: string = null;
  private accountPath: string = null;
  private tenantId: string = null;
  private ptpoId: string = null;
  private dealerIds: string = null;
  private companyCode: string = null;
  private oktaUserProfile: OktaUserProfile;
  $selectedContract: BehaviorSubject<ISelectedContract> = new BehaviorSubject<ISelectedContract>(null);
  $contracts: BehaviorSubject<IContract[]> = new BehaviorSubject<IContract[]>(null);
  $eprismContracts: BehaviorSubject<any[]> = new BehaviorSubject<any[]>(null);
  $userClaims: BehaviorSubject<UserClaims> = new BehaviorSubject<UserClaims>(null);
  $hasActiveAccount: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

  contractInfoloaded = false;

  constructor(
    @Inject(OKTA_AUTH) public oktaAuth: OktaAuth,
    private oktaAuthService: OktaAuthStateService,
    private httpClient: HttpClient,
    private preInitSettingsService: PreInitSettingsService,
    private appLoadService: PGHAppLoadService,
    private loginService: LoginService,
    private router: Router,
    private pghOktaService: PGHOktaService,
    private storageService: StorageService,
    private resourceService: ADPResourceService,
  ) {
    try {
      const settings = this.preInitSettingsService.getSettings();
      if (settings && settings.middleWare) {
        this.middleWareUrl = settings.middleWare.middlewareUrl;
        this.accountPath = settings.middleWare.accountPath;
      } else {
        console.error('Settings or settings.middleWare is undefined in LoggedInUserSettings');
      }

      this.ptpoId = settings?.ptpoId;
    } catch (error) {
      console.error('Error initializing settings in LoggedInUserSettings:', error);
    }
    this.appLoadService.isSiteResourcesLoaded.subscribe(value => {
      if (value) {
        this.tenantId = this.resourceService.getPortalSetting('Global_CTC_TenantId').Value;
        this.companyCode = this.resourceService.getPortalSetting('Global_CTC_CompanyCode').Value;
      }
    });

    combineLatest([
      this.appLoadService.isSiteResourcesLoaded,
      this.oktaAuthService.authState$
    ]).pipe(
      filter(([a, b]) => a !== null && b !== null)).subscribe(([isLoaded, authState]) => {
        this.dealerIds = this.resourceService.getPortalSetting("Global_DealerCodes")?.Value;
        if (authState.isAuthenticated && this.dealerIds) {
          if (this.contractInfoloaded === false) {
            this.contractInfoloaded = true;
            this.setInitialContract();
            this.oktaUserProfile = this.storageService.getLocal<OktaUserProfile>('okta-user-profile');
          }
        }
      });
  }

  private setInitialContract() {
    this.pghOktaService.getUser().then((userClaims: UserClaims) => {
      this.setUserClaims(userClaims);
      let elitaContracts = this.storageService.getLocal<IContract[]>('ElitaContracts');
      let eprismContracts = this.storageService.getLocal<any[]>('EprismContracts');
      if ((elitaContracts === null || elitaContracts.length === 0) && (eprismContracts === null || eprismContracts.length === 0)) {
        this.getContractsWithMembers()
          .pipe(
            // Throw error if there are no contracts
            switchMap(userContract => {
              if (!userContract?.elitaContracts?.length && !userContract?.eprismContracts?.length) {
                return throwError(new Error('No contracts found for the provided user.'));
              }
              return of(userContract);
            }),
            // If error, logout and show no contract modal on login page
            catchError((userContract) => {
                this.loginService.failedLoginBycontract = true;
                this.oktaAuth.tokenManager.clear();
                this.oktaAuth.closeSession();
                this.router.navigate(['/tech-support'],
                  { queryParams: { headerKey: 'TechSupport_NoContract'}});
                return of(null);
            })
          )
          .subscribe({
            next: (userContract) => {
              this.oktaUserProfile = userContract.oktaUserProfile;
              this.setOktaUserProfile();
              this.initializeContract(userContract.elitaContracts, userContract.eprismContracts, userClaims);
              this.storageService.setLocal('ElitaContracts', userContract.elitaContracts, true);
              this.storageService.setLocal('EprismContracts', userContract.eprismContracts, true);
            }
          });
      } else {
        this.initializeContract(elitaContracts, eprismContracts, userClaims);
        // Refresh Contracts in async mode to localstorage
        this.getContractsWithMembers()
          .pipe(
            switchMap(userContract => {
              return of(userContract);
            }),
          )
          .subscribe({
            next: (userContract) => {
              this.storageService.setLocal('ElitaContracts', userContract.elitaContracts, true);
              this.storageService.setLocal('EprismContracts', userContract.eprismContracts, true);
            }
          });
      }
    });
  }

  private initializeContract(elitaContracts: IContract[], eprismContracts: any[], userClaims: UserClaims): void {
    // Early return if no contracts available
    if (!elitaContracts?.length && !eprismContracts?.length) {
      return;
    }

    // Try to initialize with Elita contract first
    let selectedContract = this.tryInitializeElitaContract(elitaContracts, userClaims);

    // Fall back to ePrism contract if no Elita contract available or if Elita contract is expired
    if (!selectedContract || this.isContractRecentlyCancelled(selectedContract)) {
      if (!eprismContracts?.length) {
        this.loginService.failedLoginByContractCancalled = true;
        this.oktaAuth.tokenManager.clear();
        sessionStorage.clear();
        this.router.navigate(['/tech-support'], {
          queryParams: { headerKey: 'Login_FailedLoginModal_Body_Text_For_ContractCancelled' },
        });
        return;
      }

      const eContract = eprismContracts.find(x => x.policyIdentifier.mobileNumber == this.oktaUserProfile.primaryPhone);
      selectedContract = this.initializeEprismContract(eContract);
      elitaContracts = null;
    } else {
      this.loginService.failedLoginByContractCancalled = false;
    }

    this.setContracts(elitaContracts, eprismContracts);
    this.setSelectedContract(selectedContract);
    this.updateActiveAccountStatus(elitaContracts, eprismContracts);
  }

  private tryInitializeElitaContract(elitaContracts: IContract[], userClaims: UserClaims): ISelectedContract | null {
    if (!elitaContracts?.length) {
      return null;
    }

    const contract = elitaContracts.find((c) => c.id === this.loginService.selectedContractId) || elitaContracts[0];
    const contractIndex = elitaContracts.findIndex((c) => c.id === this.loginService.selectedContractId) + 1 || 1;
    const contractTitle = elitaContracts.length > 1 ? `Pocket Geek Home Plan ${contractIndex}` : 'Pocket Geek Home Plan';

    return {
      ...contract,
      isPrimary: userClaims.email.toLowerCase() === contract.primaryUserEmail.toLowerCase(),
      isExpired: contract.status === ContractStatusType.Expired,
      isCancelled: contract.status === ContractStatusType.Cancelled,
      isSuspended: contract.subscriberStatusCode === 'S',
      isPTPO: this.isPTPO(contract.dealerId),
      title: contractTitle,
      useGSSPClaims: this.useGSSPClaims(contract.dealerId),
      isEprismContract: false,
      oktaPrimaryPhone: this.oktaUserProfile.primaryPhone,
    };
  }

  private isContractRecentlyCancelled(contract: ISelectedContract): boolean {
    return contract.status === ContractStatusType.Cancelled && contract.cancellationDate && isAfter(add(new Date(), { days: -30 }), new Date(contract.cancellationDate));
  }

  private updateActiveAccountStatus(elitaContracts: IContract[], eprismContracts: any[]): void {
    const cancelledOrExpiredHasFullAccess = this.resourceService.isPortalSettingTrue('Global_CancelledOrExpiredContract_HasFullAccess');

    if (cancelledOrExpiredHasFullAccess) {
      this.$hasActiveAccount.next(true);
      return;
    }

    const hasActiveElitaContract = elitaContracts?.length ? !!elitaContracts.find((c) => c.status === ContractStatusType.Active) : false;
    const hasActiveEprismContract = eprismContracts?.length ? !!eprismContracts.find((c) => c.lineInfo.lineStatus === ContractStatusType.Active) : false;

    this.$hasActiveAccount.next(hasActiveElitaContract || hasActiveEprismContract);
  }

  public initializeEprismContract(contract: any): ISelectedContract {
    const today = new Date();
    const selectedContract: ISelectedContract = {
      oktaPrimaryPhone: this.oktaUserProfile.primaryPhone,
      isPrimary: contract.policyHolderInfo.pahPhone == contract.policyIdentifier.mobileNumber,
      isExpired: Date.parse(contract.coverageInfo.endDate) < Date.parse(today.toLocaleDateString()),
      isCancelled: Date.parse(contract.coverageInfo.endDate) < Date.parse(today.toLocaleDateString()), //TODO:Eprism - This needs to looked at
      isSuspended: contract.lineInfo.lineStatus?.toLowerCase() == "s",
      isPTPO: !!contract.additionalDetails.dealerID ? this.isPTPO(contract.additionalDetails.dealerID) : false, //TODO:Eprism - This needs to looked at
      title: `Protection 360 account`, //TODO:Eprism - This needs to looked at
      useGSSPClaims: !!contract.additionalDetails.dealerID ? this.useGSSPClaims(contract.additionalDetails.dealerID) : false, //TODO:Eprism - This needs to looked at
      dealerId: contract.additionalDetails.dealerID,
      certificateNumberForPhonesPolicy: null, //TODO:Eprism - This needs to looked at
      certificateNumberForGadgetsPolicy: null, //TODO:Eprism - This needs to looked at
      firstName: contract.policyHolderInfo.firstName,
      lastName: contract.policyHolderInfo.lastName,
      addressLine1: contract.policyHolderInfo.streetAddress1,
      city: contract.policyHolderInfo.city,
      state: contract.policyHolderInfo.state,
      zipCode: contract.policyHolderInfo.zipCode,
      id: contract.policyIdentifier.policyNumber,
      primaryUserEmail: contract.policyHolderInfo.email,
      certificateNumberForClientSoldGadgetsPolicy: null,
      startDate: contract.coverageInfo.startDate,
      expirationDate: contract.coverageInfo.endDate,
      grossPolicyPrice: 0, //TODO:Eprism - This needs to looked at
      masterPlanCode: contract.productInfo.insuranceCode, //TODO:Eprism - This needs to looked at
      status: contract.lineInfo.lineStatus,
      subscriberStatusCode: null, //TODO:Eprism - This needs to looked at
      cancellationDate: contract.coverageInfo.endDate, // in Eprism, this will be the cancellation date, will be 9999-12-31 when the contract is active
      inactiveMembers: [],
      isMobilePlanActive: true,
      sku: null,
      members: [{
        firstName: contract.policyHolderInfo.firstName,
        lastName: contract.policyHolderInfo.lastName,
        avatarUrl: contract.policyHolderInfo.avatarUrl,
        abletofileClaim: null,
        contactConsent: null,
        contractNumber: contract.policyIdentifier.policyNumber,
        customerNumber: contract.policyIdentifier.subscriberNumber, //TODO:Eprism - This needs to looked at
        emailAddress: contract.policyHolderInfo.email,
        id: contract.policyHolderInfo.ssn, //TODO:Eprism - This needs to looked at
        language: contract.policyHolderInfo.languagePreference == "E" ? "en" : contract.policyHolderInfo.languagePreference,
        phoneNumber: contract.policyIdentifier.mobileNumber,
        profileColor: contract.policyHolderInfo.profileColor,
        profileSeq: 1, //TODO:Eprism - This needs to looked at
        profileStatus: contract.lineInfo.lineStatus, //TODO:Eprism - This needs to looked at
        relationship: null,
        transactionCode: 0
      }],
      isEprismContract: true
    };
    return selectedContract;
  }

  public clearContractFromCache() {
    this.storageService.removeLocal('ElitaContracts', true);
    this.storageService.removeLocal('EprismContracts', true);
  }

  private setContracts(elitaContracts: IContract[], eprismContracts: any[]) {
    if (elitaContracts?.length) {
      this.$contracts.next(elitaContracts);
    }
    if (eprismContracts?.length) {
      this.$eprismContracts.next(eprismContracts);
    }
    this.storageService.setLocal<string>(`ElitaContractsCount`, elitaContracts?.length?.toString() ?? '0');
    this.storageService.setLocal<string>(`EprismContractsCount`, eprismContracts?.length?.toString() ?? '0');
    this.storageService.setLocal<string>(`IsEprismOnly`, !elitaContracts?.length && !!eprismContracts?.length ? 'true' : 'false');
  }

  private setUserClaims(userClaims: UserClaims) {
    this.$userClaims.next(userClaims);
  }

  private setOktaUserProfile() {
    this.storageService.setLocal('okta-user-profile', this.oktaUserProfile, true);
  }

  getCurrentUserGroup() {
    const group = new OktaGroup();
    const selectedSiteSetting = this.preInitSettingsService.getSiteSettings();
    if (localStorage.getItem(OKTA_TOKEN_STORAGE)) {
      const token: any = JSON.parse(localStorage.getItem(OKTA_TOKEN_STORAGE));
      const decodedToken = this.oktaAuth.token.decode(token.accessToken.accessToken);
      console.log(decodedToken);
      if (decodedToken && decodedToken.payload && decodedToken.payload.groups && Array.isArray(decodedToken.payload.groups) && decodedToken.payload.groups.length) {
        const isGroupExist = decodedToken.payload.groups.some(x => x.toString().toLowerCase().trim() === selectedSiteSetting.groupName.toLowerCase().trim());
        if (isGroupExist) {
          group.id = selectedSiteSetting.groupId;
          group.name = selectedSiteSetting.groupName;
        }
      }
    }
    console.log(group);
    return group;
  }

  getCurrentGroup() {
    const group = new OktaGroup();
    const selectedSiteSetting = this.preInitSettingsService.getSiteSettings();
    if (selectedSiteSetting) {
      group.id = selectedSiteSetting.groupId;
      group.name = selectedSiteSetting.groupName;
    }
    return group;
  }

  isUserInCurrentGroup() {
    let isExists = false;
    const selectedSiteSetting = this.preInitSettingsService.getSiteSettings();
    const token: any = JSON.parse(localStorage.getItem(OKTA_TOKEN_STORAGE));
    if (token && token.accessToken && token.accessToken.accessToken) {
      const decodedToken = this.oktaAuth.token.decode(token.accessToken.accessToken);
      if (decodedToken && decodedToken.payload && decodedToken.payload.groups && Array.isArray(decodedToken.payload.groups) && decodedToken.payload.groups.length) {
        isExists = decodedToken.payload.groups.some(x => x.toString().toLowerCase().trim() === selectedSiteSetting.groupName.toLowerCase().trim());
        console.log(`isExists ${isExists}`);
      }
    }
    return isExists;
  }

  setSelectedContract(selectedContract: ISelectedContract) {
    // filter out inactive members
    selectedContract.inactiveMembers = selectedContract.members.filter(m => m.profileStatus === 'I');
    selectedContract.members = selectedContract.members.filter(m => m.profileStatus !== 'I');
    this.loginService.selectedContractId = selectedContract.id;
    this.$selectedContract.next(selectedContract);
  }

  isPTPO(delaerId: string): boolean {
    return delaerId?.toLowerCase() === this.ptpoId?.toLowerCase();
  }

  getContractsWithMembers(): Observable<IUserContract> {
    const method = 'contractswithmembers';
    const oktaPrefix = this.preInitSettingsService.getSiteSettings().oktaPrefix.toUpperCase();
    const isElita = this.resourceService.getPortalSetting('Global_CTC_IsElita').Value;
    return this.httpClient.get<IUserContract>(
      `${this.middleWareUrl}${this.accountPath}${method}?isElitaActive=${isElita}&tenantId=${this.tenantId}&dealerIds=${encodeURIComponent(this.dealerIds)}&companyCode=${encodeURIComponent(this.companyCode)}&oktaPrefix=${oktaPrefix}`
    ).pipe(tap(userContract => {
      userContract?.elitaContracts?.forEach(contract => {
        contract.isMobilePlanActive = this.resourceService.isPortalSettingTrue('Global_CTC_IsMobileplanActive');
      });
    }));
  }

  isFeatureEnabled(dealerCode: string, featureKey: string): boolean {
    return this.isSettingEnabledforDealer(featureKey, dealerCode);
  }

  useGSSPClaims(dealerCode: string): boolean {
    return this.isSettingEnabledforDealer("Global_UseGSSP_Claims_ForDealer", dealerCode);
  }

  isSettingEnabledforDealer(setting: string, dealerCode: string) {
    let portalSetting = this.resourceService.getPortalSetting(setting)?.Value;
    if (portalSetting) {
      if (portalSetting.toLowerCase().trim() == 'all')
        return true;
      else if (portalSetting.toLowerCase().trim() == 'none')
        return false;
      else {
        let isEnabledFor = portalSetting.split(",");
        return isEnabledFor.some(x => x.toLowerCase().trim() === dealerCode.toLowerCase().trim());
      }
    } else return false;
  }
}
